import { Ionicons } from '@expo/vector-icons';
import { createContext, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { LayoutAnimation, Platform, Pressable, StyleSheet, Text, UIManager, View } from 'react-native';

if (Platform.OS === 'android' && UIManager.setLayoutAnimationEnabledExperimental) {
  UIManager.setLayoutAnimationEnabledExperimental(true);
}

export type IconActionKey = string;

type ActiveConfig = {
  icon: keyof typeof Ionicons.glyphMap;
  label: string;
  color: string;
  onPress: () => void;
  loading?: boolean;
};

type RowCtx = {
  requestOpen: (key: IconActionKey, config: ActiveConfig) => void;
};

const RowContext = createContext<RowCtx | null>(null);

const SIZE = 56;

function animate() {
  // LayoutAnimation δεν υποστηρίζεται σωστά στο react-native-web — προκαλούσε
  // "Failed to set an indexed property [0] on CSSStyleDeclaration".
  if (Platform.OS === 'web') return;
  LayoutAnimation.configureNext(LayoutAnimation.create(220, LayoutAnimation.Types.easeInEaseOut, LayoutAnimation.Properties.opacity));
}

/** Περιτύλιγμα για μια σειρά IconActionButton — δείχνει το πλήρες όνομα
 * του ανοιχτού κουμπιού σε μια λωρίδα ΠΑΝΩ από τη σειρά (κανονική ροή του
 * layout, όχι Modal/απόλυτη θέση) — άρα δεν κόβεται ποτέ από scroll clipping
 * ούτε βγαίνει εκτός οθόνης, και δεν προκαλεί το πάγωμα που προκαλούσε το
 * Modal σε ορισμένες συσκευές. */
export function IconActionRow({
  expandedKey, onRequestExpand, children,
}: {
  expandedKey: IconActionKey | null;
  onRequestExpand: (key: IconActionKey | null) => void;
  children: ReactNode;
}) {
  const [config, setConfigState] = useState<ActiveConfig | null>(null);

  const ctx = useMemo<RowCtx>(() => ({
    requestOpen: (key, cfg) => {
      animate();
      setConfigState(cfg);
      onRequestExpand(key);
    },
  }), [onRequestExpand]);

  function fireAction() {
    if (!config) return;
    animate();
    onRequestExpand(null);
    config.onPress();
  }

  return (
    <RowContext.Provider value={ctx}>
      <View>
        {expandedKey && config && (
          <Pressable
            onPress={fireAction}
            style={[styles.previewBar, { borderColor: config.color }]}
            accessibilityRole="button"
            accessibilityLabel={config.label}
          >
            <Ionicons name={config.icon} size={20} color={config.color} />
            <Text style={[styles.previewLabel, { color: config.color }]} numberOfLines={1}>
              {config.loading ? '…' : config.label}
            </Text>
          </Pressable>
        )}
        <View style={iconActionBarStyles.row}>{children}</View>
      </View>
    </RowContext.Provider>
  );
}

type Props = {
  actionKey: IconActionKey;
  icon: keyof typeof Ionicons.glyphMap;
  /** Πλήρες κείμενο — εμφανίζεται στη λωρίδα πάνω από τη σειρά όταν ανοίγει. */
  label: string;
  /** Σύντομο κείμενο κάτω από το εικονίδιο (π.χ. "Ακύρωση" αντί για
   * "Ακύρωση Συμφωνίας"). Αν λείπει, φαίνεται μόνο το εικονίδιο. */
  shortLabel?: string;
  /** Χρώμα αντίστοιχο της πράξης — π.χ. κόκκινο για διαφωνία, πορτοκαλί για
   * ακύρωση, χρυσό για ολοκλήρωση, aqua για επικοινωνία. */
  color: string;
  onPress: () => void;
  expandedKey: IconActionKey | null;
  /** Κάλεσέ το με το actionKey για άνοιγμα, με null για κλείσιμο. */
  onRequestExpand: (key: IconActionKey | null) => void;
  badge?: number;
  /** Μικρή κουκίδα (χωρίς αριθμό) — π.χ. για "εκκρεμεί ενημερωμένη έκδοση". */
  dot?: boolean;
  disabled?: boolean;
  loading?: boolean;
  /** 'round' → κύκλος (π.χ. chat). Προεπιλογή: τετράγωνο. */
  shape?: 'square' | 'round';
};

/** Πρέπει να βρίσκεται μέσα σε IconActionRow. 1ο πάτημα → ζητάει από τη
 * σειρά να δείξει το πλήρες όνομά του στη λωρίδα από πάνω (τονίζεται και το
 * ίδιο το κουμπί). 2ο πάτημα στο ίδιο κουμπί ή στη λωρίδα → εκτελεί την
 * πράξη. Πάτημα σε άλλο κουμπί όσο ένα είναι ανοιχτό → αλλάζει με το ίδιο
 * tap. Πάτημα οπουδήποτε αλλού (χειρίζεται ο γονέας) → κλείνει. */
export function IconActionButton({
  actionKey, icon, label, shortLabel, color, onPress, expandedKey, onRequestExpand, badge, dot, disabled, loading, shape = 'square',
}: Props) {
  const ctx = useContext(RowContext);
  const expanded = expandedKey === actionKey;

  function handlePress() {
    if (disabled || loading) return;
    if (expanded) {
      animate();
      onRequestExpand(null);
      onPress();
    } else {
      ctx?.requestOpen(actionKey, { icon, label, color, onPress, loading });
    }
  }

  return (
    <Pressable
      onPress={handlePress}
      disabled={disabled}
      style={[
        styles.base,
        shape === 'round' ? styles.round : styles.fill,
        !!shortLabel && styles.baseWithShortLabel,
        {
          backgroundColor: expanded ? `${color}45` : `${color}2E`,
          borderColor: expanded ? color : `${color}66`,
          borderWidth: expanded ? 1.5 : 1,
        },
        disabled && styles.disabled,
      ]}
      accessibilityRole="button"
      accessibilityLabel={label}
    >
      <Ionicons name={icon} size={shortLabel ? 20 : 22} color={color} />
      {!!shortLabel && (
        <Text style={[styles.shortLabel, { color }]} numberOfLines={1}>
          {shortLabel}
        </Text>
      )}
      {!!badge && badge > 0 && (
        <View style={styles.badge}>
          <Text style={styles.badgeText}>{badge > 9 ? '9+' : badge}</Text>
        </View>
      )}
      {!badge && dot && <View style={styles.dot} />}
    </Pressable>
  );
}

/** Wrapper για μια σειρά IconActionButton. */
export const iconActionBarStyles = StyleSheet.create({
  row: { flexDirection: 'row', flexWrap: 'nowrap', gap: 8, alignItems: 'center' },
});

const styles = StyleSheet.create({
  base: {
    height: SIZE,
    borderRadius: 16,
    borderWidth: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  baseWithShortLabel: {
    flexDirection: 'column',
    gap: 2,
    paddingHorizontal: 4,
  },
  shortLabel: {
    fontSize: 10,
    fontWeight: '700',
  },
  previewBar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    height: 44,
    borderRadius: 14,
    borderWidth: 1.5,
    backgroundColor: '#0D1B2A',
    marginBottom: 8,
  },
  previewLabel: {
    fontSize: 14,
    fontWeight: '700',
  },
  fill: { flex: 1 },
  round: { width: SIZE, borderRadius: SIZE / 2 },
  disabled: { opacity: 0.5 },
  badge: {
    position: 'absolute',
    top: -4,
    right: -4,
    minWidth: 15,
    height: 15,
    borderRadius: 8,
    paddingHorizontal: 3,
    backgroundColor: '#DC2626',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1.5,
    borderColor: '#0D1B2A',
  },
  badgeText: { color: '#fff', fontSize: 8, fontWeight: '800' },
  dot: {
    position: 'absolute',
    top: 2,
    right: 2,
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#FBBF24',
    borderWidth: 1,
    borderColor: '#0D1B2A',
  },
});
